import React, { useState } from 'react';
import { IconScout } from '@/components/ui/images/IconScout';
import { UseFormRegisterReturn } from 'react-hook-form';
import { Text } from '@/components/ui/text/Text';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';
import { getInputColors } from './Input.types';
import type { InputVariant, InputColor, InputType, InputColors } from './Input.types';


interface LabelInputProps {
  // Identity
  id?: string;
  'data-testid'?: string;
  'aria-label'?: string;

  // Value & Control
  type?: InputType;
  value?: string;
  onChange?: (value: string) => void;
  registration?: UseFormRegisterReturn;

  // Display
  label?: string;
  tooltipText?: string;
  placeholder?: string;
  variant?: InputVariant;
  color?: InputColor;
  fullWidth?: boolean;

  // State
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;

  // Supporting text (shown below input)
  supportingText?: string;

  // Icons
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;

  // Validation
  maxLength?: number;

  // Events
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  // Styling
  className?: string;
  inputClassName?: string;
  labelClassName?: string;

  // Escape hatch for custom colors (like PrimaryButton's customVariant)
  customColors?: Partial<InputColors>;
}

export const LabelInput: React.FC<LabelInputProps> = ({
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,

  type = 'text',
  value = '',
  onChange,
  registration,

  label,
  tooltipText,
  placeholder,
  variant = 'standard',
  color = 'default',
  fullWidth = true,

  disabled = false,
  readOnly = false,
  required = false,
  autoFocus = false,

  supportingText,

  iconLeft,
  iconRight,

  maxLength,

  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,

  className = '',
  inputClassName = '',
  labelClassName = '',

  customColors,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  const inputValue = registration ? internalValue : value;
  const hasValue = inputValue?.length > 0;
  const isActive = isFocused || hasValue || !!placeholder;

  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Get colors from pure function, then apply any custom overrides
  const baseColors = getInputColors({
    color,
    disabled,
    focused: isFocused,
    hasValue,
  });

  const inputColors: InputColors = {
    ...baseColors,
    ...customColors,
  };

  // Variant-specific styles
  const getLabelPosition = (variantType: InputVariant, hasIconLeft: boolean) => {
    if (variantType === 'standard') return 'left-0';
    return hasIconLeft ? 'left-12' : 'left-0';
  };

  const variantStyles = {
    floating: {
      container: 'relative pt-[12px]',
      input: 'bg-transparent border-0 border-b focus:ring-0 outline-none',
      label: `absolute ${getLabelPosition('floating', !!iconLeft)} pointer-events-none transition-all duration-200 will-change-transform`,
      labelPosition: (active: boolean) => active ? '-top-5' : 'top-1/2',
    },
    standard: {
      container: 'relative',
      input: 'bg-transparent border-0 border-b focus:ring-0 outline-none',
      label: 'block',
      labelPosition: () => '',
    },
  } as const;

  const currentVariant = variantStyles[variant];

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (!allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
        event.preventDefault();
        return;
      }
    }
    onKeyDown?.(event);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const finalInputType = type === 'password' && showPassword ? 'text' : type === 'number' ? 'text' : type;

  const renderPasswordToggle = () => {
    if (type !== 'password') return null;

    return (
      <div
        onClick={togglePasswordVisibility}
        className="cursor-pointer transition-colors duration-200"
        style={{ color: inputColors.icon }}
      >
        <IconScout name={showPassword ? 'eyeSlash' : 'eye'} size="lg" />
      </div>
    );
  };

  return (
    <div className={`${currentVariant.container} ${fullWidth ? 'w-full' : ''} ${className}`}>
      {/* Standard variant: label above input */}
      {variant === 'standard' && label && (
        <div className="flex items-center justify-between">
          <Text variant="small-regular" color={inputColors.label}>
            {label}
            {required && <span className="ml-1">*</span>}
            {tooltipText && <InfoBubble text={tooltipText} />}
          </Text>
        </div>
      )}

      <div className="relative">
        {/* Left icon */}
        {iconLeft && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <div className="h-6 w-6 flex items-center justify-center" style={{ color: inputColors.icon }}>
              {iconLeft}
            </div>
          </div>
        )}

        {/* Input element */}
        <input
          id={inputId}
          type={finalInputType}
          {...(type === 'number' ? { inputMode: 'numeric' as const } : {})}
          {...(registration || {})}
          value={registration ? undefined : value}
          placeholder={variant === 'standard' ? placeholder : ''}
          aria-label={ariaLabel}
          data-testid={dataTestId}
          onChange={registration
            ? (event) => {
              void registration.onChange(event);
              setInternalValue(event.target.value);
            }
            : (event) => {
              const raw = event.target.value;
              if (type === 'number' && raw !== '' && !/^-?\d*$/.test(raw)) return;
              onChange?.(raw);
            }
          }
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={onKeyUp}
          className={`
            ${currentVariant.input}
            px-0 pt-[6px] pb-[7px] text-sm
            ${fullWidth ? 'w-full' : ''}
            ${iconLeft ? 'pl-12' : ''}
            ${iconRight || type === 'password' ? 'pr-12' : ''}
            ${disabled ? 'cursor-not-allowed opacity-50' : ''}
            ${readOnly ? 'cursor-default' : ''}
            transition-all duration-200
            ${inputClassName}
          `}
          style={{
            color: inputColors.text,
            borderColor: inputColors.border,
          }}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          autoFocus={autoFocus}
          maxLength={maxLength}
        />

        {/* Floating variant: animated label */}
        {variant === 'floating' && label && (
          <label
            htmlFor={inputId}
            className={`
              ${currentVariant.label}
              ${currentVariant.labelPosition(isActive)}
              ${labelClassName}
            `}
            style={{
              color: inputColors.label,
              transform: isActive
                ? 'translateY(0) scale(1)'
                : 'translateY(-50%) scale(1.3333)',
              transformOrigin: 'left center',
            }}
          >
            <Text variant="small-regular" color={inputColors.label}>
              {label}
              {required && <span className="ml-1">*</span>}
              {tooltipText && <InfoBubble text={tooltipText} />}
            </Text>
          </label>
        )}

        {/* Right icons container */}
        <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          {iconRight && (
            <div className="h-6 w-6 flex items-center justify-center" style={{ color: inputColors.icon }}>
              {iconRight}
            </div>
          )}
          {renderPasswordToggle()}
        </div>
      </div>

      {/* Supporting text */}
      {supportingText && (
        <Text
          variant="small-regular"
          data-testid={color === 'error' ? 'error-message' : undefined}
          className="mt-1 transition-all duration-200"
          color={inputColors.supporting}
        >
          {supportingText}
        </Text>
      )}
    </div>
  );
};

export default LabelInput;
